import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import type { ProgressBarProps } from './ProgressBar.types';

/**
 * # ProgressBar
 *
 * Indikator prubehu nebo hodnoty.
 *
 * @example
 * <ProgressBar value={50} color="success" />
 * <ProgressBar variant="circular" value={75} size="lg" showLabel />
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  value = 0,
  variant = 'linear',
  size = 'md',
  color = 'primary',
  showLabel = false,
  animated = true,
  indeterminate = false,
  className,
  'data-testid': testId,
  ...props
}) => {
  // Clamp value between 0 and 100
  const clampedValue = Math.min(100, Math.max(0, value));

  // Resolve color
  const colorClass = colorClasses[color] || colorClasses['primary'];

  if (variant === 'circular') {
    // Basic implementation for circular progress
    // Note: For advanced circular progress, SVG based approach is better.
    // Simplifying for now with a basic mental model or SVG.
    // Let's use SVG for circular.

    const sizePx = {
      sm: 32,
      md: 48,
      lg: 64,
    }[size];

    const sizeClass = {
      sm: 'w-8 h-8',
      md: 'w-12 h-12',
      lg: 'w-16 h-16',
    }[size];

    const strokeWidth = {
      sm: 3,
      md: 4,
      lg: 5,
    }[size];

    const radius = (sizePx - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (clampedValue / 100) * circumference;

    return (
      <div className={cn('relative inline-flex items-center justify-center', sizeClass, className)} data-testid={testId} {...props}>
        <progress className="sr-only" max={100} value={indeterminate ? undefined : clampedValue}>
          {Math.round(clampedValue)}%
        </progress>
        <svg className="transform -rotate-90 w-full h-full" aria-hidden="true">
          {/* Background circle */}
          <circle
            className="text-gray-200 dark:text-zinc-700"
            strokeWidth={strokeWidth}
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx={sizePx / 2}
            cy={sizePx / 2}
          />
          {/* Progress circle */}
          <circle
            className={cn(
              'transition-all duration-500 ease-out',
              // Extract text color from bg class for stroke? No, simple map needed or utility.
              // We'll use a direct text color mapping for stroke.
              strokeColorClasses[color],
              indeterminate && 'animate-spin origin-center',
            )}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={indeterminate ? 0 : offset}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx={sizePx / 2}
            cy={sizePx / 2}
          />
        </svg>
        {showLabel && !indeterminate && (
          <span className="absolute text-xs font-medium text-gray-700 dark:text-zinc-200">
            {Math.round(clampedValue)}%
          </span>
        )}
      </div>
    );
  }

  // Linear variant
  return (
    <div className={cn('w-full', className)} data-testid={testId} {...props}>
      <progress className="sr-only" max={100} value={indeterminate ? undefined : clampedValue}>
        {Math.round(clampedValue)}%
      </progress>
      <div className="flex justify-between mb-1">
        {showLabel && (
          <span className="text-xs font-medium text-gray-700 dark:text-zinc-200">
            {Math.round(clampedValue)}%
          </span>
        )}
      </div>
      <div
        className={cn(
          'w-full bg-gray-200 rounded-full dark:bg-zinc-700 overflow-hidden',
          linearSizeClasses[size],
        )}
      >
        <motion.div
          className={cn(
            'h-full origin-left rounded-full transition-all duration-500',
            colorClass,
            indeterminate && 'animate-progress-indeterminate origin-left w-full',
          )}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: indeterminate ? 1 : clampedValue / 100 }}
        />
      </div>
    </div>
  );
};

// --- Styles ---

const linearSizeClasses: Record<string, string> = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
};

const colorClasses: Record<string, string> = {
  primary: 'bg-primary-600 dark:bg-primary-500',
  success: 'bg-green-600 dark:bg-green-500',
  warning: 'bg-yellow-500',
  error: 'bg-red-600 dark:bg-red-500',
  info: 'bg-cyan-600 dark:bg-cyan-500',
  neutral: 'bg-gray-600 dark:bg-zinc-500',
  offline: 'bg-zinc-600 dark:bg-zinc-500',
};

// For SVG stroke (text color equivalent)
const strokeColorClasses: Record<string, string> = {
  primary: 'text-primary-600 dark:text-primary-500',
  success: 'text-green-600 dark:text-green-500',
  warning: 'text-yellow-500',
  error: 'text-red-600 dark:text-red-500',
  info: 'text-cyan-600 dark:text-cyan-500',
  neutral: 'text-gray-600 dark:text-zinc-500',
  offline: 'text-zinc-600 dark:text-zinc-500',
};
